import React, { useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MenuBook as CourseIcon,
  Pattern as PatternIcon,
  Assignment as ExerciseIcon,
  Lightbulb as SolutionIcon,
  Psychology as SkillIcon,
  Image as ImageIcon,
  CalendarMonth as SessionIcon,
  Quiz as QuizIcon,
  EmojiEvents as ObjectiveIcon,
  Animation as AnimationIcon,
  OndemandVideo as VideoIcon,
  AccountTree as SetupIcon,
  PlaylistAddCheck as SkillSetIcon,
  School as BacIcon,
} from '@mui/icons-material';
import ExercisePatterns from './ExercisePatterns';
import Courses from './Courses';
import Exercises from './Exercises';
import Sessions from './Sessions';
import Quizzes from './Quizzes';
import Solutions from './Solutions';
import SkillSets from './SkillSets';
import Skills from './Skills';
import Objectives from './Objectives';
import Animations from './Animations';
import Videos from './Videos';
import Images from './Images';
import Setups from './Setups';
import Bac from './Bac';
import { useNavigationHistoryStore } from '../../store/navigationHistoryStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`workshop-tabpanel-${index}`}
      aria-labelledby={`workshop-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Callback registry for before tab change
const beforeTabChangeCallbacks: { [key: string]: () => Promise<void> } = {};

export const registerBeforeTabChange = (key: string, callback: () => Promise<void>) => {
  beforeTabChangeCallbacks[key] = callback;
};

export const unregisterBeforeTabChange = (key: string) => {
  delete beforeTabChangeCallbacks[key];
};

export interface PatternNavigation {
  patternId: number;
  containerId: number | null;
}

const tabDefs = [
  { label: 'Cours', icon: <CourseIcon /> },
  { label: "Patterns d'exercices", icon: <PatternIcon /> },
  { label: 'Exercices', icon: <ExerciseIcon /> },
  { label: 'Séances', icon: <SessionIcon /> },
  { label: 'Animations', icon: <AnimationIcon /> },
  { label: 'Quizz', icon: <QuizIcon /> },
  { label: 'Solutions', icon: <SolutionIcon /> },
  { label: 'Objectifs', icon: <ObjectiveIcon /> },
  { label: 'Programmes', icon: <SkillSetIcon /> },
  { label: 'Savoir-faire', icon: <SkillIcon /> },
  { label: 'Images', icon: <ImageIcon /> },
  { label: 'Vidéos', icon: <VideoIcon /> },
  { label: 'Setups', icon: <SetupIcon /> },
  { label: 'Bac', icon: <BacIcon /> },
];

const Workshop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingPatternNav, setPendingPatternNav] = useState<PatternNavigation | null>(null);
  const pushHistory = useNavigationHistoryStore((s) => s.push);

  // Read state from URL search params
  const tabValue = parseInt(searchParams.get('tab') || '1', 10);
  const urlContainerId = searchParams.get('c') ? parseInt(searchParams.get('c')!, 10) : null;
  const urlItemId = searchParams.get('item') ? parseInt(searchParams.get('item')!, 10) : null;

  // Track last params we pushed to avoid echo loops
  const lastPushedRef = useRef<string>('');

  const buildUrl = (params: Record<string, string>) => {
    const qs = Object.entries(params).map(([k, v]) => `${k}=${v}`).join('&');
    return `/workshop?${qs}`;
  };

  const updateSearchParams = useCallback((params: Record<string, string>) => {
    const sorted = Object.keys(params).sort().map(k => `${k}=${params[k]}`).join('&');
    if (sorted === lastPushedRef.current) return;
    lastPushedRef.current = sorted;
    setSearchParams(params);
  }, [setSearchParams]);

  const handleNavigateToPattern = useCallback((patternId: number, containerId: number | null) => {
    setPendingPatternNav({ patternId, containerId });
    const params: Record<string, string> = { tab: '1' };
    if (containerId !== null) params.c = containerId.toString();
    params.item = patternId.toString();
    pushHistory(buildUrl(params), `Patterns d'exercices`);
    updateSearchParams(params);
  }, [updateSearchParams, pushHistory]);

  const handleTabChange = async (_event: React.SyntheticEvent, newValue: number) => {
    const callbacks = Object.values(beforeTabChangeCallbacks);
    await Promise.all(callbacks.map(cb => cb().catch(err => console.error('Error in beforeTabChange callback:', err))));

    const params = { tab: newValue.toString() };
    pushHistory(buildUrl(params), tabDefs[newValue]?.label || 'Atelier');
    updateSearchParams(params);
  };

  // Callback for tabs to report container/item navigation changes
  const handleNavChange = useCallback((containerId: number | null, itemId: number | null, label?: string) => {
    const params: Record<string, string> = { tab: tabValue.toString() };
    if (containerId !== null) params.c = containerId.toString();
    if (itemId !== null) params.item = itemId.toString();

    const tabName = tabDefs[tabValue]?.label || 'Atelier';
    const fullLabel = label ? `${tabName} > ${label}` : tabName;
    pushHistory(buildUrl(params), fullLabel);
    updateSearchParams(params);
  }, [tabValue, updateSearchParams, pushHistory]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Atelier de Création
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="workshop tabs"
          variant="standard"
          sx={{
            minHeight: 'unset',
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {tabDefs.map((tab, index) => (
            <Tab
              key={tab.label}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{
                minHeight: 36,
                py: 0.5,
                px: 1.5,
                fontSize: '0.8rem',
                textTransform: 'none',
              }}
            />
          ))}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Courses urlContainerId={urlContainerId} urlItemId={urlItemId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <ExercisePatterns
            navigateToPattern={pendingPatternNav}
            onNavigationHandled={() => setPendingPatternNav(null)}
            urlContainerId={urlContainerId}
            urlItemId={urlItemId}
            onNavChange={handleNavChange}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Exercises urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Sessions urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Animations urlContainerId={urlContainerId} urlItemId={urlItemId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Quizzes urlContainerId={urlContainerId} urlItemId={urlItemId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <Solutions urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={7}>
          <Objectives urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={8}>
          <SkillSets />
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <Skills onNavigateToPattern={handleNavigateToPattern} />
        </TabPanel>

        <TabPanel value={tabValue} index={10}>
          <Images urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={11}>
          <Videos urlContainerId={urlContainerId} onNavChange={handleNavChange} />
        </TabPanel>

        <TabPanel value={tabValue} index={12}>
          <Setups />
        </TabPanel>

        <TabPanel value={tabValue} index={13}>
          <Bac />
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Workshop;
